import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/helper';
import {
  selectActivationCompletedCount,
  selectActivationHighlightedTask,
  selectActivationTasks,
  selectActivationTrackerCollapsed,
  selectTestLocationOptedIn,
  setActivationTrackerCollapsed,
} from '../../../redux/ownerActivationSlice';
import { selectLocations, selectValidCurrentLocation } from '../../../redux/locationSlice';
import OwnerActivationTracker, {
  type OwnerActivationTrackerTask,
} from './OwnerActivationTracker';
import {
  ACTIVATION_TASK_CONFIG,
  ACTIVATION_TASK_ORDER,
} from './activationTaskConfig';
import {
  useOwnerActivationTaskActions,
  type OwnerActivationTaskAction,
} from '../../../hooks/useOwnerActivationTaskActions';
import useLocation from '../../../hooks/useLocation';
import ownerActivationAnalytics from '../../../services/ownerActivationAnalytics';
import {
  selectActivationTrackerPhase,
  selectOwnerActivation,
} from '../../../redux/ownerActivationSlice';
import { Platform } from 'react-native';
import type { OwnerActivationAnalyticsPlatform } from '../../../services/ownerActivationAnalyticsTypes';

/**
 * Connected Home activation tracker (US5, T073). Maps activation selectors +
 * task destinations to the presentational tracker. Task rows stay visible in a
 * fixed order and the current recommended task is highlighted; Quick Actions
 * remain replaced until every task completes (handled by OwnerGuidanceSlot).
 */

export interface OwnerActivationTrackerContainerProps {
  isLocationSwitching: boolean;
  openBackOffice: () => void;
  openAddStaff: () => void;
  openLocationPicker: () => void;
  /** Tracker bootstrap loading (evidence not yet resolved). */
  loading?: boolean;
  testID?: string;
}

const OwnerActivationTrackerContainer: React.FC<
  OwnerActivationTrackerContainerProps
> = ({
  isLocationSwitching,
  openBackOffice,
  openAddStaff,
  openLocationPicker,
  loading = false,
  testID = 'owner-activation-tracker',
}) => {
  const dispatch = useDispatch();
  const { fetchLocations } = useLocation();
  const tasks = useAppSelector(selectActivationTasks);
  const completedCount = useAppSelector(selectActivationCompletedCount);
  const collapsed = useAppSelector(selectActivationTrackerCollapsed);
  const highlightedTaskId = useAppSelector(selectActivationHighlightedTask);
  const locations = useAppSelector(selectLocations);
  const currentLocation = useAppSelector(selectValidCurrentLocation);
  const currentLocationId = currentLocation?._id ?? null;
  const phase = useAppSelector(selectActivationTrackerPhase);
  const owner = useAppSelector(selectOwnerActivation);
  const testLocationOptedIn = useAppSelector(selectTestLocationOptedIn);
  const businessType = useAppSelector(
    state => state.auth.inApp.account.businessInfo?.type,
  );
  const viewedRef = React.useRef(false);

  // First sale depends on a product existing; the add_product credit is the
  // canonical signal (bootstrap credits it for pre-existing products too).
  const hasProduct = tasks.add_product.completedAt !== null;
  // A system-generated location is the safe practice ("test") location.
  const testLocationReady = useMemo(
    () => locations.some(l => l.bp_location_is_system_generated === true),
    [locations],
  );

  const actions = useOwnerActivationTaskActions({
    hasProduct,
    isLocationSwitching,
    testLocationReady,
    openBackOffice,
    openAddStaff,
    openLocationPicker,
    refreshLocations: () => {
      void fetchLocations();
    },
  });

  // Tracker funnel: view once per mount while active/unacknowledged.
  React.useEffect(() => {
    if (viewedRef.current) return;
    if (phase !== 'active' && phase !== 'completed_unacknowledged') return;
    viewedRef.current = true;
    const platform: OwnerActivationAnalyticsPlatform =
      Platform.OS === 'ios'
        ? 'ios'
        : Platform.OS === 'android'
          ? 'android'
          : 'web';
    try {
      ownerActivationAnalytics.track('activation_tracker_viewed', {
        onboarding_version: 1,
        platform,
        business_type_group: businessType || undefined,
        tracker_phase: phase,
        completed_task_count: completedCount,
        wizard_product_created: owner.wizard.productCreatedDuringWizard,
      });
    } catch {
      // ignore
    }
  }, [
    businessType,
    completedCount,
    owner.wizard.productCreatedDuringWizard,
    phase,
  ]);

  const visibleTaskOrder = useMemo(
    () =>
      testLocationOptedIn
        ? ACTIVATION_TASK_ORDER
        : ACTIVATION_TASK_ORDER.filter(id => id !== 'visit_test_location'),
    [testLocationOptedIn],
  );

  const rows = useMemo<OwnerActivationTrackerTask[]>(
    () =>
      visibleTaskOrder.map(taskId => {
        const copy = ACTIVATION_TASK_CONFIG[taskId];
        const completed = tasks[taskId].completedAt !== null;
        const action: OwnerActivationTaskAction = actions[taskId];
        const onPress = completed
          ? undefined
          : () => {
              const platform: OwnerActivationAnalyticsPlatform =
                Platform.OS === 'ios'
                  ? 'ios'
                  : Platform.OS === 'android'
                    ? 'android'
                    : 'web';
              try {
                ownerActivationAnalytics.track('activation_task_clicked', {
                  onboarding_version: 1,
                  platform,
                  business_type_group: businessType || undefined,
                  tracker_phase:
                    phase === 'inactive' ? 'active' : phase,
                  completed_task_count: completedCount,
                  wizard_product_created:
                    owner.wizard.productCreatedDuringWizard,
                  task_id: taskId,
                });
              } catch {
                // ignore
              }
              action.onPress();
            };
        return {
          id: taskId,
          label: copy.label,
          description: copy.description,
          completed,
          highlighted: !completed && highlightedTaskId === taskId,
          ctaLabel: copy.ctaLabel,
          dependencyNote: action.dependencyNote,
          disabled: action.disabled,
          busy: action.busy,
          onPress,
        };
      }),
    [
      actions,
      businessType,
      completedCount,
      highlightedTaskId,
      owner.wizard.productCreatedDuringWizard,
      phase,
      tasks,
      visibleTaskOrder,
    ],
  );

  const onToggleCollapsed = useCallback(
    (next: boolean) => {
      if (!currentLocationId) return;
      dispatch(
        setActivationTrackerCollapsed({
          locationId: currentLocationId,
          collapsed: next,
          locationType: 'real',
        }),
      );
      const platform: OwnerActivationAnalyticsPlatform =
        Platform.OS === 'ios'
          ? 'ios'
          : Platform.OS === 'android'
            ? 'android'
            : 'web';
      const props = {
        onboarding_version: 1 as const,
        platform,
        business_type_group: businessType || undefined,
        tracker_phase:
          phase === 'inactive'
            ? ('active' as const)
            : phase,
        completed_task_count: completedCount,
        wizard_product_created: owner.wizard.productCreatedDuringWizard,
      };
      try {
        ownerActivationAnalytics.track(
          next ? 'activation_tracker_collapsed' : 'activation_tracker_expanded',
          props,
        );
      } catch {
        // ignore
      }
    },
    [
      businessType,
      completedCount,
      currentLocationId,
      dispatch,
      owner.wizard.productCreatedDuringWizard,
      phase,
    ],
  );

  return (
    <OwnerActivationTracker
      testID={testID}
      tasks={rows}
      completedCount={completedCount}
      totalCount={visibleTaskOrder.length}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      loading={loading}
    />
  );
};

export default OwnerActivationTrackerContainer;
